import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserProfile, getUserProfileByUsername } from '../services/api';
import { UserProfile } from '../types/UserProfile';

/**
 * プロフィールの取得口を1本にまとめる。
 *
 * **以前は自分だけ React Query、他人は useState + useEffect の二本立てだった。**
 * そのせいで
 *   - 他人のプロフィールはキャッシュされず、戻るたびに白紙から取り直し
 *   - ユーザー間を遷移したとき、次の人のレスポンスが来るまで**前の人が表示され続ける**
 *     (親が state をクリアしても、子が `if (src)` ガードで無視していた)
 * という2つの問題が出ていた。
 *
 * キャッシュのキーがユーザーごとに分かれていれば、別人のページを開いた瞬間の
 * data は必ず undefined になる。**前の人が残るのは構造的に起こり得なくなる。**
 * サーバー状態をローカル state へ写し直さないこと。写した瞬間に同じ問題が戻る。
 */
export const profileQueryKey = (username?: string) =>
  username ? (['profile', username] as const) : (['profile', 'me'] as const);

// 他人のプロフィールは自分のものより長めに新鮮とみなす。
// 自分のページは編集直後の再訪が多いので短い。
const STALE_TIME = { own: 10_000, other: 30_000 };

/**
 * @param username 未指定なら自分のプロフィール(`/profile/`)
 * @param enabled  認証待ちなどで取得を止めたいとき false
 */
export const useProfileQuery = (username?: string, enabled = true) =>
  useQuery({
    queryKey: profileQueryKey(username),
    queryFn: username
      ? () => getUserProfileByUsername(username)
      : async () => (await getUserProfile()).data,
    enabled,
    staleTime: username ? STALE_TIME.other : STALE_TIME.own,
  });

/**
 * プロフィールのキャッシュを部分更新する。
 *
 * 楽観更新(フォロー / バナー差し替え等)はここを通すこと。
 * ローカル state に持つと表示とキャッシュが二重管理になる。
 */
export const useProfileCacheUpdater = (username?: string) => {
  const queryClient = useQueryClient();
  const key = profileQueryKey(username);

  return {
    /** 一部のフィールドだけ差し替える。キャッシュが空なら何もしない */
    patch: (patch: Partial<UserProfile>) =>
      queryClient.setQueryData<UserProfile>(key, prev => (prev ? { ...prev, ...patch } : prev)),
    /** 今の値を使って更新する(件数の増減など)。キャッシュが空なら何もしない */
    update: (updater: (prev: UserProfile) => UserProfile) =>
      queryClient.setQueryData<UserProfile>(key, prev => (prev ? updater(prev) : prev)),
    /** 丸ごと差し替える(PATCH のレスポンス等) */
    replace: (next: UserProfile) => queryClient.setQueryData<UserProfile>(key, next),
    /** 次に見られたとき取り直させる */
    invalidate: () => queryClient.invalidateQueries({ queryKey: key }),
  };
};
